import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "as_start_project_draft_v1";

const EMPTY_DRAFT = {
  // Step 1 — project details
  category: "",
  quantity: "",
  target_price: "",
  materials: "",
  destination: "",
  timeline: "",
  reference_notes: "",
  reference_files: [], // [{name, size, type}] metadata (files not persisted)
  // Step 2 — contact details
  name: "",
  company: "",
  email: "",
  website: "",
  phone: "",
};

const StartProjectContext = createContext({
  isOpen: false,
  open: () => {},
  close: () => {},
  draft: EMPTY_DRAFT,
  setDraft: () => {},
  reset: () => {},
});

const loadDraft = () => {
  if (typeof window === "undefined") return EMPTY_DRAFT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw);
    return { ...EMPTY_DRAFT, ...parsed };
  } catch {
    return EMPTY_DRAFT;
  }
};

const persistDraft = (draft) => {
  if (typeof window === "undefined") return;
  try {
    // Don't persist reference file objects (browser File instances are non-serialisable).
    const safe = { ...draft, reference_files: (draft.reference_files || []).map((f) => ({ name: f.name, size: f.size, type: f.type })) };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch {
    /* quota exceeded or storage disabled — ignore */
  }
};

export const StartProjectProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraftState] = useState(EMPTY_DRAFT);
  const [suspendPersist, setSuspendPersist] = useState(false);

  // Hydrate from localStorage on mount (client only).
  useEffect(() => {
    setDraftState(loadDraft());
  }, []);

  // Persist on any change (skipped for one tick immediately after reset).
  useEffect(() => {
    if (suspendPersist) {
      setSuspendPersist(false);
      return;
    }
    persistDraft(draft);
  }, [draft, suspendPersist]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const setDraft = useCallback((updater) => {
    setDraftState((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
  }, []);
  const reset = useCallback(() => {
    // Suspend the persist effect for the next state transition so the
    // localStorage entry is genuinely cleared (not immediately overwritten
    // by an EMPTY_DRAFT dump).
    setSuspendPersist(true);
    setDraftState(EMPTY_DRAFT);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, []);

  // Escape key to close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open.
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = original;
    };
  }, [isOpen, close]);

  const value = useMemo(
    () => ({ isOpen, open, close, draft, setDraft, reset }),
    [isOpen, open, close, draft, setDraft, reset]
  );

  return <StartProjectContext.Provider value={value}>{children}</StartProjectContext.Provider>;
};

export const useStartProject = () => useContext(StartProjectContext);
